/**
 * Multi-Chart Display Module
 * マルチチャート表示 - 複数銘柄を同時にグリッド表示
 */

import { state, saveState } from '../state.js';
import { fetchStockData } from '../api/index.js';

// Layout configurations
const LAYOUTS = {
    '1x1': { rows: 1, cols: 1, total: 1 },
    '1x2': { rows: 1, cols: 2, total: 2 },
    '2x1': { rows: 2, cols: 1, total: 2 },
    '2x2': { rows: 2, cols: 2, total: 4 }
};

// DOM Elements (cached on init)
let multiChartContainer, layoutSelector;
let chartPanes = [];

/**
 * Initialize Multi-Chart Module
 */
export function initMultiChart() {
    multiChartContainer = document.getElementById('multi-chart-container');
    layoutSelector = document.getElementById('layout-selector');

    if (!multiChartContainer) return;

    // Initialize state if not exists
    if (!state.multiChart) {
        state.multiChart = {
            enabled: false,
            layout: '1x1',
            panes: [{ symbol: null, period: '1M' }]
        };
    }

    // Event Listeners
    if (layoutSelector) {
        layoutSelector.addEventListener('change', (e) => {
            changeLayout(e.target.value);
        });
    }

    // Initial render based on saved state
    if (state.multiChart.enabled) {
        enableMultiChart();
        setLayout(state.multiChart.layout);
    }
}

/**
 * Enable multi-chart mode
 */
export function enableMultiChart() {
    state.multiChart.enabled = true;

    // Hide standard chart container
    const standardChart = document.getElementById('chart-container');
    if (standardChart) standardChart.classList.add('hidden');

    // Show multi-chart container
    if (multiChartContainer) {
        multiChartContainer.classList.remove('hidden');
    }

    saveState();
}

/**
 * Disable multi-chart mode (return to standard)
 */
export function disableMultiChart() {
    state.multiChart.enabled = false;

    // Clear panes
    clearAllPanes();

    // Hide multi-chart container
    if (multiChartContainer) {
        multiChartContainer.classList.add('hidden');
    }

    // Show standard chart container
    const standardChart = document.getElementById('chart-container');
    if (standardChart) standardChart.classList.remove('hidden');

    saveState();
}

/**
 * Change layout configuration
 */
export function changeLayout(layoutKey) {
    if (!LAYOUTS[layoutKey]) return;

    state.multiChart.layout = layoutKey;
    setLayout(layoutKey);
    saveState();
}

/**
 * Set layout and create panes
 */
function setLayout(layoutKey) {
    const layout = LAYOUTS[layoutKey];
    if (!layout || !multiChartContainer) return;

    // Clear existing panes
    clearAllPanes();

    // Update container grid
    multiChartContainer.style.gridTemplateColumns = `repeat(${layout.cols}, 1fr)`;
    multiChartContainer.style.gridTemplateRows = `repeat(${layout.rows}, 1fr)`;

    // Create new panes
    for (let i = 0; i < layout.total; i++) {
        const pane = createChartPane(i);
        multiChartContainer.appendChild(pane);
        chartPanes.push(pane);
    }

    // Restore symbols if saved
    if (state.multiChart.panes) {
        state.multiChart.panes.forEach((savedPane, index) => {
            if (savedPane.symbol && chartPanes[index]) {
                loadSymbolInPane(index, savedPane.symbol, savedPane.period);
            }
        });
    }
}

/**
 * Create a chart pane element
 */
function createChartPane(index) {
    const pane = document.createElement('div');
    pane.className = 'chart-pane';
    pane.dataset.index = index;
    pane.innerHTML = `
        <div class="pane-header">
            <input type="text" class="pane-symbol-input" placeholder="Symbol..." maxlength="10">
            <select class="pane-period-select">
                <option value="1D">1D</option>
                <option value="1W">1W</option>
                <option value="1M" selected>1M</option>
                <option value="3M">3M</option>
                <option value="1Y">1Y</option>
            </select>
        </div>
        <div class="pane-chart" id="pane-chart-${index}"></div>
        <div class="pane-info">
            <span class="pane-price">-</span>
            <span class="pane-change">-</span>
        </div>
    `;

    // Event listeners for this pane
    const symbolInput = pane.querySelector('.pane-symbol-input');
    const periodSelect = pane.querySelector('.pane-period-select');

    symbolInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
            const symbol = symbolInput.value.trim().toUpperCase();
            if (symbol) {
                loadSymbolInPane(index, symbol, periodSelect.value);
            }
        }
    });

    periodSelect.addEventListener('change', () => {
        const symbol = symbolInput.value.trim().toUpperCase();
        if (symbol) {
            loadSymbolInPane(index, symbol, periodSelect.value);
        }
    });

    return pane;
}

/**
 * Load a symbol in a specific pane
 */
async function loadSymbolInPane(paneIndex, symbol, period = '1M') {
    const pane = chartPanes[paneIndex];
    if (!pane) return;

    const chartContainer = pane.querySelector('.pane-chart');
    const symbolInput = pane.querySelector('.pane-symbol-input');
    const priceEl = pane.querySelector('.pane-price');
    const changeEl = pane.querySelector('.pane-change');

    symbolInput.value = symbol;
    priceEl.textContent = 'Loading...';
    changeEl.textContent = '';

    try {
        const data = await fetchStockData(symbol, period);

        if (data && data.candleData && data.candleData.length > 0) {
            // Create/update chart in pane
            renderPaneChart(chartContainer, data.candleData);

            // Update price info
            const latest = data.candleData[data.candleData.length - 1];
            const first = data.candleData[0];
            const change = latest.close - first.open;
            const changePct = (change / first.open) * 100;

            priceEl.textContent = `$${latest.close.toFixed(2)}`;
            changeEl.textContent = `${change >= 0 ? '+' : ''}${change.toFixed(2)} (${changePct.toFixed(2)}%)`;
            changeEl.className = `pane-change ${change >= 0 ? 'positive' : 'negative'}`;

            // Save state
            if (!state.multiChart.panes) state.multiChart.panes = [];
            state.multiChart.panes[paneIndex] = { symbol, period };
            saveState();
        }
    } catch (error) {
        priceEl.textContent = 'Error';
        changeEl.textContent = error.message;
        console.warn(`Failed to load ${symbol}:`, error);
    }
}

/**
 * Render chart in a pane using Lightweight Charts
 */
function renderPaneChart(container, candleData) {
    // Clear previous chart
    container.innerHTML = '';

    // Check if LightweightCharts is available
    if (typeof LightweightCharts === 'undefined') {
        container.innerHTML = '<p class="chart-error">Chart library not loaded</p>';
        return;
    }

    const chart = LightweightCharts.createChart(container, {
        width: container.clientWidth,
        height: container.clientHeight || 200,
        layout: {
            background: { type: 'solid', color: 'transparent' },
            textColor: '#8b949e',
        },
        grid: {
            vertLines: { color: 'rgba(139, 148, 158, 0.1)' },
            horzLines: { color: 'rgba(139, 148, 158, 0.1)' },
        },
        crosshair: { mode: LightweightCharts.CrosshairMode.Normal },
        rightPriceScale: { borderColor: '#30363d' },
        timeScale: {
            borderColor: '#30363d',
            timeVisible: true,
        },
    });

    const candleSeries = chart.addCandlestickSeries({
        upColor: '#3fb950',
        downColor: '#f85149',
        borderUpColor: '#3fb950',
        borderDownColor: '#f85149',
        wickUpColor: '#3fb950',
        wickDownColor: '#f85149',
    });

    candleSeries.setData(candleData);
    chart.timeScale().fitContent();

    // Handle resize
    const resizeObserver = new ResizeObserver(() => {
        chart.applyOptions({
            width: container.clientWidth,
            height: container.clientHeight
        });
    });
    resizeObserver.observe(container);
}

/**
 * Clear all panes
 */
function clearAllPanes() {
    chartPanes.forEach(pane => pane.remove());
    chartPanes = [];
    if (multiChartContainer) {
        multiChartContainer.innerHTML = '';
    }
}

/**
 * Toggle multi-chart mode
 */
export function toggleMultiChart() {
    if (state.multiChart.enabled) {
        disableMultiChart();
    } else {
        enableMultiChart();
        setLayout(state.multiChart.layout || '1x1');
    }
}
